import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Visibility,
  Add,
  Search,
  Phone,
  Email,
  LocationOn,
  Business,
  Receipt,
  MoreVert,
  Edit,
  Delete,
  ShoppingCart,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      // Since there's no dedicated /api/clients endpoint, we'll get users with client role
      const response = await api.get('/auth/users');
      const users = response.data?.users || response.data || [];
      // Filter only client role users with profile data
      const clientUsers = users.filter(user => user.role === 'client' && user.profile);
      setClients(clientUsers);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar('Failed to fetch clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuOpen = (event, client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleOpenDeleteDialog = (client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedClient(null);
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;

    try {
      await api.delete(`/auth/users/${selectedClient.id}`);
      showSnackbar('Client deleted successfully', 'success');
      handleCloseDeleteDialog();
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      showSnackbar('Failed to delete client', 'error');
    }
  };

  const handleViewSales = (clientId) => {
    // Navigate to sales page filtered by client
    navigate(`/sales?client=${clientId}`);
    handleMenuClose();
  };

  const handleViewInvoices = (clientId) => {
    // Navigate to invoices page filtered by client
    navigate(`/invoices?client=${clientId}`);
    handleMenuClose();
  };

  const getFilteredClients = () => {
    return clients.filter((client) => {
      const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      const phone = client.phone || '';
      const address = client.profile?.address?.toLowerCase() || '';
      const city = client.profile?.city?.toLowerCase() || '';

      const search = searchQuery.toLowerCase();

      return (
        fullName.includes(search) ||
        email.includes(search) ||
        phone.includes(search) ||
        address.includes(search) ||
        city.includes(search)
      );
    });
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getSalesCount = (clientProfile) => {
    // This would need to come from the API - placeholder for now
    return clientProfile?.sales_count || 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredClients = getFilteredClients();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Clients
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage clients with signed contracts
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/leads')}
        >
          Convert Lead
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, phone, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Typography variant="body2" color="text.secondary" alignSelf="center">
                  {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact Information</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Estate Sales</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {getInitials(client.first_name, client.last_name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {client.first_name} {client.last_name}
                            </Typography>
                            {client.profile?.preferred_contact && (
                              <Chip
                                label={`Prefers ${client.profile.preferred_contact}`}
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <Email fontSize="small" color="action" />
                            {client.email}
                          </Typography>
                          {client.phone && (
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                              <Phone fontSize="small" color="action" />
                              {client.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {client.profile?.address ? (
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                              <LocationOn fontSize="small" color="action" />
                              {client.profile.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" ml={3}>
                              {client.profile.city && client.profile.state
                                ? `${client.profile.city}, ${client.profile.state} ${client.profile.zip_code || ''}`
                                : '-'}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No address
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<ShoppingCart fontSize="small" />}
                          label={`${getSalesCount(client.profile)} sales`}
                          size="small"
                          color={getSalesCount(client.profile) > 0 ? 'primary' : 'default'}
                          onClick={() => handleViewSales(client.id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {client.created_at
                          ? format(new Date(client.created_at), 'MMM dd, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="More actions">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, client)}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={6}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {searchQuery ? 'No clients found' : 'No clients yet'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          {searchQuery
                            ? 'Try adjusting your search criteria'
                            : 'Convert leads to clients when they sign contracts'}
                        </Typography>
                        {!searchQuery && (
                          <Button
                            variant="contained"
                            startIcon={<Business />}
                            onClick={() => navigate('/leads')}
                          >
                            View Leads
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedClient && handleViewClient(selectedClient)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedClient && handleViewSales(selectedClient.id)}>
          <ListItemIcon>
            <ShoppingCart fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Estate Sales</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedClient && handleViewInvoices(selectedClient.id)}>
          <ListItemIcon>
            <Receipt fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Invoices</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => selectedClient && handleOpenDeleteDialog(selectedClient)}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Client</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Client Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Client Details
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                      {getInitials(selectedClient.first_name, selectedClient.last_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedClient.first_name} {selectedClient.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Client since {selectedClient.created_at
                          ? format(new Date(selectedClient.created_at), 'MMMM yyyy')
                          : 'Unknown'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                    <Email fontSize="small" />
                    {selectedClient.email}
                  </Typography>
                </Grid>

                {selectedClient.phone && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Phone
                    </Typography>
                    <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                      <Phone fontSize="small" />
                      {selectedClient.phone}
                    </Typography>
                  </Grid>
                )}

                {selectedClient.profile?.preferred_contact && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Preferred Contact Method
                    </Typography>
                    <Typography variant="body1">
                      {selectedClient.profile.preferred_contact}
                    </Typography>
                  </Grid>
                )}

                {selectedClient.profile?.address && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Address
                    </Typography>
                    <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" />
                      {selectedClient.profile.address}
                      {selectedClient.profile.city && (
                        <>, {selectedClient.profile.city}, {selectedClient.profile.state} {selectedClient.profile.zip_code}</>
                      )}
                    </Typography>
                    {selectedClient.profile.county && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        County: {selectedClient.profile.county}
                      </Typography>
                    )}
                  </Grid>
                )}

                {selectedClient.profile?.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedClient.profile.notes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<ShoppingCart />}
                      onClick={() => {
                        setViewDialogOpen(false);
                        handleViewSales(selectedClient.id);
                      }}
                    >
                      View Estate Sales
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Receipt />}
                      onClick={() => {
                        setViewDialogOpen(false);
                        handleViewInvoices(selectedClient.id);
                      }}
                    >
                      View Invoices
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete the client and their user account.
          </Alert>
          <Typography>
            Are you sure you want to delete {selectedClient?.first_name} {selectedClient?.last_name}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteClient} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;
